import { emitHintSignal } from '@flobi/asset-library/hints';
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Icon, ICON_SIZE, type IconName } from '../chrome/icons';
import { tip } from '../../ui/tooltip';
import type { CatalogLayers, DropPoint, LibraryPaint, LibraryPicture, LibraryShape, LibraryText } from '../useEditorCanvas';
import { carry } from '../carry';
import { capSentence, kindOf, listMedia, overCap, VIDEO_CAP, type DriveMedia, type KindChoice, type StuffPlace } from '../../services/library';
import { listVisibleProjects as listProjectInfos } from '../../projects/projectsApi';
import { PopUpButton } from '../../ui/controls';
import { PhoneEmptyState, usePhone } from '../../home/PhoneEmptyState';
import { LibraryFilters, SearchField } from './LibraryFilters';
export { SearchField } from './LibraryFilters';
import { useKit, useKitFonts, useWork } from '../work';
import type { BrandKit, KitTypeStyle } from '../../services/work';
import { tileRatio, twoColumns } from './columns';
import { LibraryPagination, PanelHeader } from './parts';
import { svgText } from './svgText';
import { readableMediaUrl } from '../../services/mediaRead';
import { readClip } from '../../canvas/clips';
import { ProjectFolders } from './ProjectFolders';

/**
 * What each kind on the Assets rail shows. Every tile here is a real thing: pressing it
 * puts that thing on the page. There are no stand-ins — a kind with nothing behind it is
 * not on the rail (see `Library.tsx`).
 */

/** What Assets can do to the page, handed down from the editor. */
/**
 * What Assets does to the page. Everything that puts something on it takes an optional
 * `at`: where it was dragged to and let go (`../carry.ts`). Without one it lands in the
 * middle of the page being worked on.
 */
export interface LibraryActions {
  projectId?: string | null;
  useElement?(ref: import('../../services/elements').ElementRef): void;
  addShape(shape: LibraryShape, at?: DropPoint): void;
  addText(preset: LibraryText, at?: DropPoint): void;
  addPicture(picture: LibraryPicture, at?: DropPoint): void;
  /** Opens the file dialog; what is chosen is uploaded and placed. */
  upload(): void;
  /** The pictures this file already holds. */
  filePictures(): LibraryPicture[];
  /** The artboard being worked on, following its current selection and size. */
  currentArtboard?(): { width: number; height: number } | null;
  /** The page being worked on in a few plain lines (pageSummary.ts), for the Templates panel's category. */
  pageSummary?(): string | null;
  /** Paints what is chosen — or, with nothing chosen, what is drawn next — with a colour, or a brand's gradient. */
  useColour(paint: LibraryPaint): void;
  /** A graphic or an icon from the catalogue, onto the page: `share` of the page's shorter side across. */
  placeDrawing(svg: string, options: { name: string; share: number; at?: DropPoint }): void;
  /** A graphic kept as layers, onto the page. */
  placeLayers(layers: CatalogLayers, size: { width: number; height: number }, at?: DropPoint): void;
  /** A template: into the page when it is empty, else as a new page. */
  placeTemplate(template: { name: string; width: number; height: number; background: string | null; svg?: string | null; layers?: CatalogLayers | null }, at?: DropPoint): void;
  /** What is chosen, as the catalogue keeps a reusable graphic. */
  clipSelection(): { name: string; layers: CatalogLayers; width: number; height: number } | null;
  /** Says something to the person, briefly. */
  notice(message: string): void;
}

/**
 * The same actions, but everything placed lands at `at`: how a tile dragged onto the page
 * places itself exactly as a press does, only where it was let go.
 */
export function aimed(actions: LibraryActions, at: DropPoint): LibraryActions {
  return {
    ...actions,
    addShape: (shape) => actions.addShape(shape, at),
    addText: (preset) => actions.addText(preset, at),
    addPicture: (picture) => actions.addPicture(picture, at),
    placeDrawing: (svg, options) => actions.placeDrawing(svg, { ...options, at }),
    placeLayers: (layers, size) => actions.placeLayers(layers, size, at),
    placeTemplate: (template) => actions.placeTemplate(template, at),
  };
}

/** A panel's scrolling body: one text edge, the same padding in every kind. */
export function Body({ children, onEnd, label, className = '' }: { children: ReactNode; onEnd?: () => void; label?: string; className?: string }) {
  return (
    <div
      // Room at the top for the ring a tile wears when the pointer is over it: flush with the edge, the scroll area cut it off.
      role={label ? 'region' : undefined}
      aria-label={label}
      tabIndex={label ? 0 : undefined}
      className={`library-body ui-scroll min-h-0 min-w-0 flex-1 overflow-x-hidden px-[12px] pb-[14px] pt-[3px] ${className}`}
      onKeyDown={event => {
        if (label && /^(Arrow(?:Up|Down|Left|Right)|PageUp|PageDown|Home|End| )$/.test(event.key)) event.stopPropagation();
      }}
      onScroll={onEnd ? (event) => {
        const el = event.currentTarget;
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 240) onEnd();
      } : undefined}
    >
      {children}
    </div>
  );
}

export function Heading({ children }: { children: ReactNode }) {
  return <h3 className="mb-[8px] mt-[14px] text-[13px] font-semibold text-label first:mt-[2px]">{children}</h3>;
}

/**
 * Said once, quietly, where a list would be: that there is nothing, or that it could not be read. On a
 * phone, where Assets is a sheet, it is the suite's empty state at sheet size (the empty-states pass,
 * pick E, 2026-10-26); a laptop keeps the quiet line.
 */
export function Quiet({ children, tone = 'empty' }: { children: ReactNode; tone?: 'empty' | 'error' }) {
  const phone = usePhone();
  if (phone) return <PhoneEmptyState size="sheet" tone={tone} title={children} line={null} />;
  return <p className="px-[4px] py-[18px] text-[12px] text-label-2">{children}</p>;
}

export { tileUrl } from './tileUrl';
import { tileUrl } from './tileUrl';

/** A picture's size as the tile knows it: what its row says, else what the tile's own image turned out to be. */
function sized<T extends { width: number; height: number }>(picture: T, tile: HTMLElement): T {
  if (picture.width > 0 && picture.height > 0) return picture;
  const image = tile.querySelector('img');
  return image && image.naturalWidth > 0 ? { ...picture, width: image.naturalWidth, height: image.naturalHeight } : picture;
}

/** A picture to press. Its own shape, never a square it was not. */
function PictureTile({ src, name, ratio, onPick, carries }: { src: string; name: string; ratio: number; onPick: (tile: HTMLElement, at?: DropPoint) => void; carries?: string }) {
  return (
    <button
      type="button"
      data-flobi-media={carries}
      onClick={(event) => onPick(event.currentTarget)}
      {...carry((at, tile) => onPick(tile, at))}
      {...tip(name)}
      aria-label={name}
      className="block w-full overflow-hidden rounded-[9px] bg-fill-2 shadow-[0_0_0_1px_var(--separator)] transition-shadow hover:shadow-[0_0_0_2px_var(--accent)]"
      style={ratio > 0 ? { aspectRatio: String(tileRatio(ratio)) } : { minHeight: 60 }}
    >
      <img src={src} alt="" loading="lazy" draggable={false} className={ratio > 0 ? 'block size-full object-cover' : 'block w-full'} />
    </button>
  );
}

/** Pictures two abreast at their own shapes, each standing where it was first put (`twoColumns`). */
export function Wall<T>({ items, ratio, tile }: { items: readonly T[]; ratio: (item: T) => number; tile: (item: T) => ReactNode }) {
  const columns = twoColumns(items.map(ratio));
  return (
    <div className="library-wall flex items-start gap-[8px]">
      {columns.map((column, side) => (
        <div key={side} className="flex w-0 min-w-0 flex-1 flex-col gap-[8px]">
          {column.map((index) => tile(items[index]!))}
        </div>
      ))}
    </div>
  );
}

const shapeOf = (picture: { width: number; height: number }) => (picture.width > 0 && picture.height > 0 ? picture.width / picture.height : 0);

// ── My Stuff ────────────────────────────────────────────────────────────────

/** A place in My Stuff, a level down from its first view. */
type Place = { kind: 'drive'; place: StuffPlace; title: string; mediaKind?: KindChoice } | { kind: 'projects' } | { kind: 'file' };

/** Recent, as it was last read this visit: My Stuff opens on it, so it is shown at once and read again behind it. */
let lastRecent: DriveMedia[] | null = null;
/** How many of the newest things My Stuff opens on. */
const RECENT_FIRST = 6;

/**
 * My Stuff's overview and every collection use the shared library search/filter layout.
 * Every level opened stays mounted under the one on top, hidden, so Back lands where you
 * were — same scroll, same search, same list — instead of reading it all again from the top.
 */
export function MyStuffPanel({ actions, onClose }: { actions: LibraryActions; onClose: () => void }) {
  const [stack, setStack] = useState<Place[]>([]);
  const open = (place: Place) => setStack((was) => [...was, place]);
  const back = () => setStack((was) => was.slice(0, -1));
  const levels: ReactNode[] = [
    <>
      <PanelHeader title="My Stuff" onClose={onClose} />
      <StuffHome actions={actions} open={open} />
    </>,
    ...stack.map((place, depth) => {
      const under = depth === 0 ? 'My Stuff' : stack[depth - 1]?.kind === 'projects' ? 'Projects' : 'My Stuff';
      if (place.kind === 'projects') return <>
        <PanelHeader title="Projects" back={back} backLabel={under} onClose={onClose} />
        <ProjectList open={project => open({ kind: 'drive', place: { project: project.id }, title: project.name })} />
      </>;
      if (place.kind === 'file') return <>
        <PanelHeader title="In This File" back={back} backLabel={under} onClose={onClose} />
        <FilePlace actions={actions} />
      </>;
      return <>
        <PanelHeader title={place.title} back={back} backLabel={under} onClose={onClose} />
        <DrivePlace key={JSON.stringify(place.place)} place={place.place} title={place.title} initialKind={place.mediaKind} actions={actions} />
      </>;
    }),
  ];
  const top = levels.length - 1;
  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      {levels.map((level, depth) => (
        <div
          key={depth}
          className={`absolute inset-0 flex min-h-0 flex-col ${depth < top ? 'invisible pointer-events-none' : ''}`}
          // Not reachable by keyboard or screen reader while another level is over it.
          aria-hidden={depth < top || undefined}
          inert={depth < top}
        >
          {level}
        </div>
      ))}
    </div>
  );
}

/** My Stuff's first view: search, what you had last, and the places. */
function StuffHome({ actions, open }: { actions: LibraryActions; open: (place: Place) => void }) {
  const [query, setQuery] = useState('');
  const [recent, setRecent] = useState<{ items: DriveMedia[] | null; problem?: string }>({ items: lastRecent });
  const filePictures = actions.filePictures().length;

  useEffect(() => {
    let live = true;
    listMedia({ place: 'recent' })
      .then((page) => {
        lastRecent = page.items;
        if (live) setRecent({ items: page.items });
      })
      .catch((error: unknown) => live && setRecent((was) => ({ items: was.items ?? [], problem: error instanceof Error ? error.message : 'My Stuff could not be read.' })));
    return () => {
      live = false;
    };
  }, []);

  const row = (key: string, icon: IconName, name: string, place: Place) => (
    <button key={key} type="button" onClick={() => open(place)} className="flex min-h-[46px] items-center gap-[11px] rounded-[10px] px-[6px] py-[5px] text-left transition-colors hover:bg-fill-2">
      <span className="grid size-[34px] shrink-0 place-items-center rounded-[9px] bg-fill-1 text-label">
        <Icon name={icon} size={ICON_SIZE.control} />
      </span>
      <span className="min-w-0 flex-1 truncate text-[13.5px] font-medium text-label">{name}</span>
      <span className="text-label-3">
        <Icon name="chevron-right" size={12} />
      </span>
    </button>
  );
  const groups = [
    [
      row('uploads', 'upload', 'Uploads', { kind: 'drive', place: 'uploads', title: 'Uploads' }),
      row('generated', 'sparkles', 'Generated', { kind: 'drive', place: 'generated', title: 'Generated' }),
      filePictures > 0 ? row('file', 'artboard', 'In This File', { kind: 'file' }) : null,
    ],
    [row('projects', 'folder', 'Projects', { kind: 'projects' })],
    [row('workspace', 'people', 'Workspace', { kind: 'drive', place: 'workspace', title: 'Workspace' })],
  ].map((group) => group.filter(Boolean)).filter((group) => group.length > 0);

  return (
    <>
      <SearchField value={query} onChange={setQuery} label="Search my stuff" />
      {query.trim() ? (
        // Searching from the top looks through everything of yours at once.
        <DrivePlace key="search" place="recent" title="My Stuff" search={query} actions={actions} />
      ) : (
        <Body label="My Stuff library">
          <div className="mb-[8px] mt-[2px] flex items-baseline">
            <h3 className="text-[13px] font-semibold text-label">Recent</h3>
            <span className="flex-1" />
            {recent.items && recent.items.length > RECENT_FIRST ? (
              <button type="button" onClick={() => open({ kind: 'drive', place: 'recent', title: 'Recent' })} className="flex items-center gap-[1px] text-[12px] font-medium text-accent-text hover:underline">
                See All
                <Icon name="chevron-right" size={11} />
              </button>
            ) : null}
          </div>
          {recent.problem && !recent.items?.length ? (
            <Quiet tone="error">{recent.problem}</Quiet>
          ) : recent.items === null ? (
            // Where the things will be, while they are asked for: plain blocks, not pretend pictures.
            <div aria-hidden className="grid grid-cols-2 gap-[12px]">
              {Array.from({ length: RECENT_FIRST }, (_, index) => (
                <span key={index} className="aspect-square rounded-[7px] bg-fill-2" />
              ))}
            </div>
          ) : recent.items.length === 0 ? (
            <Quiet>What you upload and make in Flobi shows here.</Quiet>
          ) : (
            <div className="grid grid-cols-2 gap-[12px]">
              {recent.items.slice(0, RECENT_FIRST).map((media) => (
                <MediaTile key={media.id} media={media} square actions={actions} />
              ))}
            </div>
          )}
          <div className="mt-[16px] grid gap-[6px]">
            {groups.map((group, index) => (
              <div key={index} className={`-mx-[6px] grid gap-[2px] ${index > 0 ? 'border-t border-separator pt-[6px]' : ''}`}>
                {group}
              </div>
            ))}
          </div>
        </Body>
      )}
    </>
  );
}

/** Your projects, the file's own first: each opens onto what is in its Drive folder. */
function ProjectList({ open }: { open: (project: { id: string; name: string }) => void }) {
  const { context } = useWork();
  const defaultOrder = context?.project ? 'current' : 'name';
  const [query, setQuery] = useState('');
  const [order, setOrder] = useState<'current' | 'name' | 'name-desc'>(defaultOrder);
  const [projects, setProjects] = useState<{ id: string; name: string; cover?: string | null }[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    listProjectInfos().then(all => live && setProjects([...all.values()])).catch((error: unknown) => live && setProblem(error instanceof Error ? error.message : 'Projects could not be read.'));
    return () => { live = false; };
  }, [context?.project?.id]);
  const visible = useMemo(() => projects?.filter(project => project.name.toLowerCase().includes(query.trim().toLowerCase())).sort((a, b) => order === 'current' ? Number(b.id === context?.project?.id) - Number(a.id === context?.project?.id) || a.name.localeCompare(b.name) : (order === 'name-desc' ? -1 : 1) * a.name.localeCompare(b.name)) ?? [], [projects, query, order, context?.project?.id]);
  return <>
    <LibraryFilters query={query} onQuery={setQuery} searchLabel="Search projects" filterLabel="Filter projects" dialogLabel="Project filters" count={Number(order !== defaultOrder)} onReset={() => setOrder(defaultOrder)}>
      <div className="catalog-filter-field"><span>Order</span><PopUpButton label="Project order" value={order} onChange={setOrder} className="catalog-filter-picker" options={[
        ...(context?.project ? [['current', 'Current project first'] as const] : []), ['name', 'Name (A–Z)'], ['name-desc', 'Name (Z–A)'],
      ]} /></div>
    </LibraryFilters>
    <Body label="Project folders">{problem ? <Quiet tone="error">{problem}</Quiet> : projects === null ? <div role="status" aria-label="Loading projects"><div aria-hidden className="grid grid-cols-2 gap-[14px]">{[0, 1, 2, 3].map(index => <span key={index} className="aspect-[128/100] rounded-[10px] bg-fill-2" />)}</div></div> : visible.length === 0 ? <Quiet>{query.trim() ? `No projects found for “${query.trim()}”.` : 'No projects yet.'}</Quiet> : <ProjectFolders projects={visible} onOpen={open} />}</Body>
  </>;
}

const MEDIA_KIND_OPTIONS: readonly (readonly [KindChoice, string])[] = [['all', 'All media'], ['image', 'Images'], ['video', 'Videos'], ['svg', 'SVG']];
const NONE: Record<KindChoice, string> = { all: 'Nothing here yet.', image: 'No images here.', video: 'No videos here.', svg: 'No SVGs here.' };

function MediaFilters({ title, query, onQuery, kind, onKind, options = MEDIA_KIND_OPTIONS }: {
  title: string; query: string; onQuery: (query: string) => void; kind: KindChoice; onKind: (kind: KindChoice) => void; options?: typeof MEDIA_KIND_OPTIONS;
}) {
  return <LibraryFilters query={query} onQuery={onQuery} searchLabel={`Search ${title.toLowerCase()}`} filterLabel={`Filter ${title.toLowerCase()}`} dialogLabel={`${title} filters`} count={Number(kind !== 'all')} onReset={() => onKind('all')}>
    <div className="catalog-filter-field"><span>Media type</span><PopUpButton label="Media type" value={kind} onChange={onKind} options={options} className="catalog-filter-picker" /></div>
  </LibraryFilters>;
}

function FilePlace({ actions }: { actions: LibraryActions }) {
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState<KindChoice>('all');
  const words = query.trim().toLowerCase();
  const pictures = actions.filePictures().filter(picture => (!words || picture.name.toLowerCase().includes(words)) && (kind === 'all' || (kindOf(picture.mime) ?? 'image') === kind));
  return <>
    <MediaFilters title="In this file" query={query} onQuery={setQuery} kind={kind} onKind={setKind} options={MEDIA_KIND_OPTIONS.filter(([value]) => value !== 'video')} />
    <Body label="File media">{pictures.length ? <Wall items={pictures} ratio={shapeOf} tile={picture => <PictureTile key={picture.assetId} src={tileUrl(picture.src)} name={picture.name} ratio={shapeOf(picture)} onPick={(tile, at) => actions.addPicture(sized(picture, tile), at)} carries={JSON.stringify({ assetId: picture.assetId, url: picture.src, name: picture.name, type: 'image', width: picture.width || undefined, height: picture.height || undefined })} />} /> : <Quiet>{words ? `Nothing called “${query.trim()}”.` : NONE[kind]}</Quiet>}</Body>
  </>;
}

/** A collection keeps its paged Drive query while sharing the media-type filter menu. */
function DrivePlace({ place, title, search, mediaKind, initialKind = 'all', actions }: { place: StuffPlace; title: string; search?: string; mediaKind?: KindChoice; initialKind?: KindChoice; actions: LibraryActions }) {
  const [query, setQuery] = useState('');
  const [localKind, setKind] = useState<KindChoice>(initialKind);
  const kind = mediaKind ?? localKind;
  const words = (search ?? query).trim();
  const [state, setState] = useState<{ items: DriveMedia[]; next: string | null; status: 'loading' | 'ready' | 'more' | 'more-failed' | 'failed'; problem?: string }>({ items: [], next: null, status: 'loading' });
  /** Which question the answer on screen belongs to: a slow answer to an old one is dropped. */
  const asked = useRef(0);
  const reading = useRef<number | null>(null);

  useEffect(() => {
    const mine = ++asked.current;
    reading.current = null;
    setState({ items: [], next: null, status: 'loading' });
    // Typing is not asking: the list is read once the hand stops.
    const wait = setTimeout(() => {
      listMedia({ place, kind, search: words })
        .then((page) => asked.current === mine && setState({ items: page.items, next: page.next, status: 'ready' }))
        .catch((error: unknown) => asked.current === mine && setState({ items: [], next: null, status: 'failed', problem: error instanceof Error ? error.message : 'My Stuff could not be read.' }));
    }, words ? 280 : 0);
    return () => { clearTimeout(wait); if (asked.current === mine) asked.current++; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(place), kind, words]);

  const more = (retry = false) => {
    if ((state.status !== 'ready' && !(retry && state.status === 'more-failed')) || !state.next || reading.current !== null) return;
    const mine = asked.current;
    reading.current = mine;
    setState((was) => ({ ...was, status: 'more', problem: undefined }));
    listMedia({ place, kind, search: words, cursor: state.next })
      .then((page) => asked.current === mine && setState((was) => ({ items: [...was.items, ...page.items], next: page.next, status: 'ready' })))
      .catch(() => asked.current === mine && setState((was) => ({ ...was, status: 'more-failed', problem: 'More items could not be loaded. Try again.' })))
      .finally(() => { if (reading.current === mine) reading.current = null; });
  };

  return (
    <>
      {search === undefined ? <MediaFilters title={title} query={query} onQuery={setQuery} kind={kind} onKind={setKind} /> : null}
      <Body label={`${title} media`} onEnd={more}>
        {state.status === 'failed' ? (
          <Quiet tone="error">{state.problem}</Quiet>
        ) : state.status === 'loading' ? (
          <div aria-hidden>
            <Wall items={[1.3, 0.8, 1, 1.4, 0.75, 1.2]} ratio={(one) => one} tile={(one) => <div key={one} className="rounded-[9px] bg-fill-2" style={{ aspectRatio: String(one) }} />} />
          </div>
        ) : state.items.length === 0 ? (
          state.next === null ? <Quiet>{words ? `Nothing called “${words}”.` : NONE[kind]}</Quiet> : null
        ) : (
          <>
            <Wall items={state.items} ratio={shapeOf} tile={(media) => <MediaTile key={media.id} media={media} actions={actions} />} />
            {state.items.some((one) => one.kind === 'video') ? <p className="mt-[12px] text-center text-[11.5px] text-label-3">{`Clips up to ${VIDEO_CAP.seconds} seconds and ${VIDEO_CAP.megabytes} MB go on a page.`}</p> : null}
          </>
        )}
        {state.status !== 'loading' && state.status !== 'failed' && (state.items.length > 0 || state.next !== null) && <LibraryPagination label="items" hasMore={state.next !== null} loading={state.status === 'more'} problem={state.status === 'more-failed' ? state.problem : undefined} onMore={() => more(true)} />}
      </Body>
    </>
  );
}

/**
 * A picture, clip or SVG to press. A picture goes on the page as itself, an SVG as the
 * drawing it is (layers, like a catalogue graphic), a clip as a picture layer that plays
 * with the page. A clip wears a play mark; one over the cap is shown dimmed with the reason,
 * and one found to be too long when it is opened is not placed.
 */
function MediaTile({ media, square = false, actions }: { media: DriveMedia; square?: boolean; actions: LibraryActions }) {
  const [busy, setBusy] = useState(false);
  const held = overCap(media);
  const pick = async (tile: HTMLElement, acts = actions) => {
    // Placed from My Stuff (Drive), pressed or dragged: its hint is done (lib/hints).
    emitHintSignal('mystuff.placed');
    if (media.kind === 'image') {
      acts.addPicture(sized({ assetId: media.assetId, mediaAssetId: media.assetId, name: media.name, src: media.src, width: media.width, height: media.height, ...(media.mime ? { mime: media.mime } : {}) }, tile));
      return;
    }
    if (busy) return;
    if (media.kind === 'video') {
      if (held) {
        acts.notice(capSentence(media.name, held));
        return;
      }
      setBusy(true);
      try {
        // How long it runs is in the file, not in Drive's list: read before it is placed.
        const facts = await readClip(await readableMediaUrl(media.src, media.assetId));
        const tooLong = overCap({ ...media, duration: facts.duration });
        if (tooLong) acts.notice(capSentence(media.name, tooLong));
        else acts.addPicture({ assetId: media.assetId, mediaAssetId: media.assetId, name: media.name, src: media.src, width: facts.width, height: facts.height, ...(media.mime ? { mime: media.mime } : {}), duration: Math.round(facts.duration * 1000) / 1000 });
      } catch (error) {
        acts.notice(`${media.name} could not be opened: ${(error as Error).message}.`);
      } finally {
        setBusy(false);
      }
      return;
    }
    setBusy(true);
    try {
      acts.placeDrawing(await svgText(await readableMediaUrl(media.src, media.assetId)), { name: media.name, share: SVG_SHARE });
    } catch (error) {
      acts.notice(`${media.name} could not be read: ${(error as Error).message}`);
    } finally {
      setBusy(false);
    }
  };
  const ratio = shapeOf(media);
  return (
    <button
      type="button"
      aria-busy={busy}
      // What this tile is to another app (the kit's dock drag): the file itself, not its thumbnail.
      data-flobi-media={JSON.stringify({ assetId: media.assetId, url: media.src, name: media.name, type: media.kind === 'video' ? 'video' : 'image', mime: media.mime, width: media.width || undefined, height: media.height || undefined })}
      onClick={(event) => void pick(event.currentTarget)}
      {...carry((at, tile) => void pick(tile, aimed(actions, at)))}
      {...tip(held ? `${media.name} — ${held}` : media.name)}
      aria-label={media.name}
      className={`relative block w-full overflow-hidden shadow-[0_0_0_1px_var(--separator)] transition-shadow hover:shadow-[0_0_0_2px_var(--accent)] ${square ? 'aspect-square rounded-[7px]' : 'rounded-[9px]'} ${media.kind === 'svg' ? 'bg-white' : 'bg-fill-2'} ${held ? 'opacity-45' : ''}`}
      style={square ? undefined : ratio > 0 ? { aspectRatio: String(tileRatio(ratio)) } : { minHeight: 60 }}
    >
      <img
        src={media.thumb}
        alt=""
        loading="lazy"
        draggable={false}
        className={media.kind === 'svg' ? 'block size-full object-contain p-[10%]' : square || ratio > 0 ? 'block size-full object-cover' : 'block w-full'}
      />
      {media.kind === 'video' ? (
        <span className="absolute bottom-[6px] left-[6px] grid size-[22px] place-items-center rounded-full bg-black/60 pl-[1px] text-white">
          <Icon name="play" size={ICON_SIZE.indicator} />
        </span>
      ) : media.kind === 'svg' ? (
        <span className="absolute bottom-[6px] left-[6px] rounded-[6px] bg-black/60 px-[6px] py-[2px] text-[10.5px] font-semibold tracking-[0.02em] text-white">SVG</span>
      ) : null}
    </button>
  );
}

/** How much of the page's shorter side an SVG from My Stuff lands across. */
const SVG_SHARE = 0.4;

// ── Shapes ──────────────────────────────────────────────────────────────────

/** A regular polygon's corners in a box of 24, one corner up — as the engine draws it. */
function corners(sides: number, inner?: number): string {
  const count = inner === undefined ? sides : sides * 2;
  const out: string[] = [];
  for (let i = 0; i < count; i++) {
    const r = inner !== undefined && i % 2 === 1 ? 10.5 * inner : 10.5;
    const a = (Math.PI * 2 * i) / count - Math.PI / 2;
    out.push(`${(12 + r * Math.cos(a)).toFixed(2)},${(12 + r * Math.sin(a)).toFixed(2)}`);
  }
  return out.join(' ');
}

const SHAPES: readonly { name: string; shape: LibraryShape; art: ReactNode }[] = [
  { name: 'Square', shape: { type: 'rect' }, art: <rect x="2.5" y="2.5" width="19" height="19" /> },
  { name: 'Rounded Square', shape: { type: 'rect', round: 0.18 }, art: <rect x="2.5" y="2.5" width="19" height="19" rx="4.5" /> },
  { name: 'Circle', shape: { type: 'ellipse' }, art: <circle cx="12" cy="12" r="10" /> },
  { name: 'Triangle', shape: { type: 'polygon', sides: 3 }, art: <polygon points={corners(3)} /> },
  { name: 'Diamond', shape: { type: 'polygon', sides: 4 }, art: <polygon points={corners(4)} /> },
  { name: 'Pentagon', shape: { type: 'polygon', sides: 5 }, art: <polygon points={corners(5)} /> },
  { name: 'Hexagon', shape: { type: 'polygon', sides: 6 }, art: <polygon points={corners(6)} /> },
  { name: 'Octagon', shape: { type: 'polygon', sides: 8 }, art: <polygon points={corners(8)} /> },
  { name: 'Star', shape: { type: 'star', points: 5, innerRatio: 0.382 }, art: <polygon points={corners(5, 0.382)} /> },
  { name: 'Sparkle', shape: { type: 'star', points: 4, innerRatio: 0.3 }, art: <polygon points={corners(4, 0.3)} /> },
  { name: 'Burst', shape: { type: 'star', points: 12, innerRatio: 0.78 }, art: <polygon points={corners(12, 0.78)} /> },
  { name: 'Line', shape: { type: 'line' }, art: <rect x="2" y="11" width="20" height="2" rx="1" /> },
];

/** The shapes the engine draws, as themselves. Pressing one puts it in the middle of the page. */
export function ShapesPanel({ actions }: { actions: LibraryActions }) {
  return (
    <Body>
      <div className="grid grid-cols-4 gap-[8px] pt-[2px]">
        {SHAPES.map((one) => (
          <button
            key={one.name}
            type="button"
            aria-label={one.name}
            {...tip(one.name)}
            onClick={() => actions.addShape(one.shape)}
            {...carry((at) => actions.addShape(one.shape, at))}
            className="grid aspect-square place-items-center rounded-[10px] bg-fill-2 text-label transition-colors hover:bg-fill-selected"
          >
            <svg viewBox="0 0 24 24" className="size-[54%] fill-current opacity-90" aria-hidden>
              {one.art}
            </svg>
          </button>
        ))}
      </div>
    </Body>
  );
}

// ── Text ────────────────────────────────────────────────────────────────────

const TEXTS: readonly { preset: LibraryText; className: string }[] = [
  { preset: { text: 'Add a heading', size: 0.09, weight: 700 }, className: 'text-[24px] font-bold leading-[28px]' },
  { preset: { text: 'Add a subheading', size: 0.05, weight: 600 }, className: 'text-[16px] font-semibold leading-[20px]' },
  { preset: { text: 'Add a little bit of body text', size: 0.03, weight: 400 }, className: 'text-[13px] leading-[18px] text-label-2' },
];

/** How big the largest of a brand's styles is set on the page, as a share of its width: a heading's. */
const LARGEST = 0.09;
const SMALLEST = 0.014;

/**
 * Lines of type to add. With a brand in force they are the BRAND'S styles — its own
 * names, faces, weights, leading and spacing, in the proportions of its scale, the
 * largest set as a heading is. Without one, three plain lines.
 */
export function TextPanel({ actions }: { actions: LibraryActions }) {
  const kit = useKit();
  useKitFonts(kit);
  const styles = kit?.styles ?? [];
  if (!kit || styles.length === 0) {
    return (
      <Body>
        <div className="grid gap-[6px] pt-[2px]">
          {TEXTS.map((one) => (
            <button key={one.preset.text} type="button" onClick={() => actions.addText(one.preset)} {...carry((at) => actions.addText(one.preset, at))} className={`rounded-[10px] bg-fill-2 px-[12px] py-[10px] text-left text-label transition-colors hover:bg-fill-selected ${one.className}`}>
              {one.preset.text}
            </button>
          ))}
        </div>
      </Body>
    );
  }

  return (
    <Body>
      <Heading>{kit.name}</Heading>
      <KitStyles kit={kit} actions={actions} />
    </Body>
  );
}

/** Puts a line of text on the page in one of the brand's styles: its face, weight, leading, spacing and case. */
export function addKitText(actions: LibraryActions, kit: BrandKit, style: KitTypeStyle, text = style.role): void {
  // The brand's own type put on the page: the hint that points at it is done (lib/hints).
  emitHintSignal('brand.placed');
  const largest = Math.max(...kit.styles.map((one) => one.size));
  const font = kit.fonts.find((one) => one.family === style.family);
  actions.addText({
    text,
    size: Math.max(SMALLEST, (LARGEST * style.size) / largest),
    weight: style.weight,
    ...(font ? { font } : {}),
    lineHeight: style.lineHeight,
    spacing: style.spacing,
    textCase: style.case,
  });
}

/** How a brand's style is drawn in the panel: in its own face, at its own place in the scale, within what a row can hold. */
export function kitTextLook(kit: BrandKit, style: KitTypeStyle, most = 26): CSSProperties {
  const largest = Math.max(...kit.styles.map((one) => one.size));
  return {
    fontFamily: kit.fonts.some((one) => one.family === style.family) ? `"${style.family}", system-ui, sans-serif` : undefined,
    fontWeight: style.weight,
    fontSize: Math.round(Math.max(12, Math.min(most, (most * style.size) / largest))),
    lineHeight: 1.2,
    letterSpacing: `${style.spacing}em`,
    textTransform: style.case === 'upper' ? 'uppercase' : style.case === 'lower' ? 'lowercase' : undefined,
  };
}

/** Every style of a brand's type scale, each a line to add. `meta` says the face and size beside each. */
export function KitStyles({ kit, actions, meta = false }: { kit: BrandKit; actions: LibraryActions; meta?: boolean }) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-[6px]">
      {kit.styles.map((style, index) => (
        <button
          // A brand may name two styles alike ("Body" at two sizes).
          key={`${style.role}-${index}`}
          type="button"
          {...tip(`${style.family} ${style.weight}`)}
          onClick={() => addKitText(actions, kit, style)}
          {...carry((at) => addKitText(aimed(actions, at), kit, style))}
          className="flex items-baseline gap-[10px] rounded-[10px] bg-fill-2 px-[12px] py-[9px] text-left text-label transition-colors hover:bg-fill-selected"
        >
          <span className="min-w-0 flex-1 truncate" style={kitTextLook(kit, style)}>{style.role}</span>
          {meta ? <span className="shrink-0 text-[11px] text-label-3">{`${style.family} ${style.size}`}</span> : null}
        </button>
      ))}
    </div>
  );
}
